import { useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Add, Delete, Download } from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const COLORS = {
  primary: '#2F4F4F',
  primaryLight: 'rgba(47, 79, 79, 0.05)',
  primaryDark: '#1c2f2f',
  secondary: '#8B7355',
  secondaryLight: 'rgba(139, 115, 85, 0.1)',
  white: '#FFFFFF',
  gray: '#666666',
  accent: '#3CB371'
};

const CalculatorWrapper = styled.div`
  max-width: 1200px;
  margin: 4rem auto;
  padding: 0 2rem;
  
  @media print {
    margin: 0;
    padding: 0;
    .no-print {
      display: none;
    }
  }
`;

const CalculatorTitle = styled.h1`
  color: ${COLORS.primary};
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    right: 50%;
    transform: translateX(50%);
    width: 100px;
    height: 3px;
    background: ${COLORS.primary};
  }
`;

const CalculatorContent = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const TracksSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const TrackCard = styled(motion.div)`
  background: ${props => props.theme.isDarkMode ? '#2a2a2a' : '#fff'};
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.small};
`;

const TrackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h3 {
    color: ${COLORS.primary};
    margin: 0;
  }
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.6rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  font-size: 0.9rem;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
  }
`;

const Select = styled.select`
  padding: 0.6rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
`;

const ResultsSidebar = styled.div`
  position: sticky;
  top: 100px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SummaryCard = styled.div`
  background: ${COLORS.primary};
  color: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  
  h2 {
    font-size: 2.5rem;
    margin: 0.5rem 0;
  }
  
  p {
    opacity: 0.8;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const StatItem = styled.div`
  background: ${props => props.theme.colors.lightGray};
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  
  h4 {
    margin: 0;
    color: ${COLORS.gray};
    font-size: 0.8rem;
  }
  
  p {
    margin: 0.3rem 0 0;
    font-weight: bold;
    color: ${props => props.theme.colors.text};
  }
`;

const Button = styled.button`
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
`;

const AddButton = styled(Button)`
  background: ${COLORS.primaryLight};
  color: ${COLORS.primary};
  border: 1px dashed ${COLORS.primary};
  width: 100%;
  
  &:hover {
    background: ${COLORS.primaryLight};
    opacity: 0.8;
  }
`;

const PrintButton = styled(Button)`
  background: ${COLORS.secondary};
  color: white;
  width: 100%;
`;

const TRACK_TYPES = [
  { value: 'fixed', label: 'קבועה לא צמודה' },
  { value: 'fixed_indexed', label: 'קבועה צמודה' },
  { value: 'prime', label: 'פריים' },
  { value: 'variable', label: 'משתנה כל 5 שנים' }
];

const Calculator = () => {
  const [tracks, setTracks] = useState([
    { id: 1, type: 'prime', amount: 300000, years: 30, rate: 6.0 },
    { id: 2, type: 'fixed', amount: 400000, years: 20, rate: 4.5 }
  ]);

  const addTrack = () => {
    setTracks([...tracks, {
      id: Date.now(),
      type: 'fixed',
      amount: 100000,
      years: 25,
      rate: 4.0
    }]);
  };

  const removeTrack = (id) => {
    if (tracks.length > 1) {
      setTracks(tracks.filter(t => t.id !== id));
    }
  };

  const updateTrack = (id, field, value) => {
    setTracks(tracks.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const calculatedResults = useMemo(() => {
    let totalMonthly = 0;
    let totalPrincipal = 0;
    let totalInterest = 0;
    
    const trackDetails = tracks.map(track => {
      const p = parseFloat(track.amount) || 0;
      const r = (parseFloat(track.rate) || 0) / 100 / 12;
      const n = (parseFloat(track.years) || 0) * 12;
      
      let monthly = 0;
      if (r > 0) {
        monthly = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      } else {
        monthly = p / n;
      }
      
      const total = monthly * n;
      const interest = total - p;
      
      totalMonthly += monthly;
      totalPrincipal += p;
      totalInterest += interest;
      
      return { ...track, monthly, interest, total };
    });

    return {
      totalMonthly,
      totalPrincipal,
      totalInterest,
      totalPayment: totalPrincipal + totalInterest,
      trackDetails
    };
  }, [tracks]);

  const doughnutData = {
    labels: ['קרן', 'ריבית'],
    datasets: [{
      data: [calculatedResults.totalPrincipal, calculatedResults.totalInterest],
      backgroundColor: [COLORS.primary, COLORS.secondary],
      borderWidth: 0
    }]
  };

  const barData = {
    labels: tracks.map((_, i) => `מסלול ${i + 1}`),
    datasets: [
      {
        label: 'קרן',
        data: calculatedResults.trackDetails.map(t => t.amount),
        backgroundColor: COLORS.primary
      },
      {
        label: 'ריבית',
        data: calculatedResults.trackDetails.map(t => t.interest),
        backgroundColor: COLORS.secondary
      }
    ]
  };

  return (
    <CalculatorWrapper>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="no-print"
      >
        <CalculatorTitle>מחשבון משכנתא מתקדם</CalculatorTitle>
      </motion.div>

      <CalculatorContent>
        <TracksSection className="no-print">
          <AnimatePresence>
            {tracks.map((track, index) => (
              <TrackCard
                key={track.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <TrackHeader>
                  <h3>מסלול {index + 1}</h3>
                  <IconButton onClick={() => removeTrack(track.id)} style={{ color: '#ff4444' }}>
                    <Delete />
                  </IconButton>
                </TrackHeader>
                
                <InputGrid>
                  <InputGroup>
                    <Label>סוג מסלול</Label>
                    <Select 
                      value={track.type}
                      onChange={(e) => updateTrack(track.id, 'type', e.target.value)}
                    >
                      {TRACK_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </Select>
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>סכום (₪)</Label>
                    <Input 
                      type="number"
                      value={track.amount}
                      onChange={(e) => updateTrack(track.id, 'amount', e.target.value)}
                    />
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>שנים</Label>
                    <Input 
                      type="number"
                      value={track.years}
                      onChange={(e) => updateTrack(track.id, 'years', e.target.value)}
                    />
                  </InputGroup>
                  
                  <InputGroup>
                    <Label>ריבית (%)</Label>
                    <Input 
                      type="number"
                      step="0.1"
                      value={track.rate}
                      onChange={(e) => updateTrack(track.id, 'rate', e.target.value)}
                    />
                  </InputGroup>
                </InputGrid>
              </TrackCard>
            ))}
          </AnimatePresence>
          
          <AddButton onClick={addTrack}>
            <Add /> הוספת מסלול למשכנתא
          </AddButton>
        </TracksSection>

        <ResultsSidebar>
          <SummaryCard>
            <p>החזר חודשי כולל</p>
            <h2>₪{Math.round(calculatedResults.totalMonthly).toLocaleString()}</h2>
            <p>למשך {Math.max(...tracks.map(t => t.years))} שנים</p>
          </SummaryCard>

          <StatsGrid>
            <StatItem>
              <h4>סך הקרן</h4>
              <p>₪{Math.round(calculatedResults.totalPrincipal).toLocaleString()}</p>
            </StatItem>
            <StatItem>
              <h4>סך הריבית</h4>
              <p>₪{Math.round(calculatedResults.totalInterest).toLocaleString()}</p>
            </StatItem>
            <StatItem>
              <h4>סך הכל החזר</h4>
              <p>₪{Math.round(calculatedResults.totalPayment).toLocaleString()}</p>
            </StatItem>
            <StatItem>
              <h4>יחס החזר</h4>
              <p>{((calculatedResults.totalPayment / calculatedResults.totalPrincipal) || 0).toFixed(2)}</p>
            </StatItem>
          </StatsGrid>

          <div style={{ height: '250px', marginTop: '1rem' }}>
            <Doughnut 
              data={doughnutData}
              options={{ 
                maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom' } }
              }}
            />
          </div>

          <div className="no-print">
            <PrintButton onClick={() => window.print()}>
              <Download /> הורדת הצעה כ-PDF
            </PrintButton>
          </div>
        </ResultsSidebar>
      </CalculatorContent>

      <div style={{ marginTop: '4rem' }} className="no-print">
        <h3 style={{ marginBottom: '2rem', textAlign: 'center' }}>התפלגות מסלולים</h3>
        <div style={{ height: '400px' }}>
          <Bar 
            data={barData}
            options={{
              maintainAspectRatio: false,
              scales: {
                x: { stacked: true },
                y: { stacked: true }
              }
            }}
          />
        </div>
      </div>
    </CalculatorWrapper>
  );
};

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(0,0,0,0.05);
  }
`;

export default Calculator;
